import { useId } from 'react'
import type { ApiRouletteSlice } from './types'
import styles from './ApiRoulette.module.css'

export function ApiRouletteSliceTable({
  slices,
  disabled,
  onChange,
}: {
  slices: ApiRouletteSlice[]
  disabled: boolean
  onChange: (slices: ApiRouletteSlice[]) => void
}) {
  const descriptionId = useId()
  const prizes = slices.filter((slice) => slice.kind === 'prize').length
  const update = (id: string, patch: Partial<ApiRouletteSlice>) =>
    onChange(slices.map((slice) => (slice.id === id ? { ...slice, ...patch } : slice)))

  return (
    <div className={styles.sliceConfiguration}>
      <div className={styles.sliceToolbar}>
        <div>
          <h4>Configuração de cada fatia</h4>
          <p>Uma linha por posição da roleta. Edite o conteúdo e as cores lado a lado.</p>
        </div>
      </div>
      <div className={styles.sliceLegend}>
        <span className={styles.prizeLegend}>{prizes} com prêmio</span>
        <span>{slices.length - prizes} sem prêmio</span>
        <span className={styles.scrollHint}>
          Deslize a tabela para ver todas as colunas.
        </span>
      </div>
      <div
        className={styles.tableWrap}
        role="region"
        aria-label="Configuração das fatias"
        tabIndex={0}
      >
        <table className={styles.sliceTable}>
          <caption className={styles.srOnly}>
            Configuração de cada fatia da Roleta API
          </caption>
          <thead>
            <tr>
              <th scope="col">Fatia</th>
              <th scope="col">Texto na roleta</th>
              <th scope="col">Resultado</th>
              <th scope="col">Descrição do prêmio</th>
              <th scope="col">Cor de fundo</th>
              <th scope="col">Cor do texto</th>
            </tr>
          </thead>
          <tbody>
            {slices.map((slice, index) => (
              <tr key={slice.id} data-kind={slice.kind}>
                <th scope="row">
                  <span className={styles.slicePosition}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </th>
                <td>
                  <input
                    aria-label={`Texto da fatia ${index + 1}`}
                    value={slice.label}
                    maxLength={80}
                    disabled={disabled}
                    onChange={(event) => update(slice.id, { label: event.target.value })}
                  />
                </td>
                <td>
                  <select
                    aria-label={`Resultado da fatia ${index + 1}`}
                    value={slice.kind}
                    disabled={disabled}
                    aria-describedby={descriptionId}
                    onChange={(event) =>
                      update(slice.id, {
                        kind: event.target.value as ApiRouletteSlice['kind'],
                      })
                    }
                  >
                    <option value="prize">Com prêmio</option>
                    <option value="lose">Sem prêmio</option>
                  </select>
                </td>
                <td>
                  <input
                    aria-label={`Descrição do prêmio da fatia ${index + 1}`}
                    value={slice.description ?? ''}
                    maxLength={500}
                    disabled={disabled}
                    placeholder="Ex.: vale-compras de R$ 50"
                    onChange={(event) =>
                      update(slice.id, { description: event.target.value })
                    }
                  />
                </td>
                {(['color', 'text_color'] as const).map((field) => (
                  <td key={field}>
                    <div className={styles.colorControl}>
                      <input
                        type="color"
                        aria-label={`${field === 'color' ? 'Cor de fundo' : 'Cor do texto'} da fatia ${index + 1}`}
                        value={slice[field]}
                        disabled={disabled}
                        onChange={(event) =>
                          update(slice.id, { [field]: event.target.value })
                        }
                      />
                      <code aria-hidden="true">{slice[field].toUpperCase()}</code>
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p id={descriptionId} className={styles.helperNote}>
        <strong>Com prêmio:</strong> pode ser escolhida nos horários vencedores.{' '}
        <strong>Sem prêmio:</strong> usada nos giros que não ganham. Mantenha ao menos uma
        de cada tipo e preserve as fatias vinculadas à agenda.
      </p>
    </div>
  )
}
